import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import {
  Settings as SettingsIcon,
  Save,
  RefreshCw,
  Clock,
  Activity,
  HardDrive,
  Package,
  Users,
  Network,
  Shield,
  Heart,
  AlertCircle,
  CheckCircle,
  X
} from 'lucide-react';

const Settings = ({ onClose, embedded = false }) => {
  const [activeTab, setActiveTab] = useState('client-settings');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  
  // Collection frequency settings
  const [collectionSettings, setCollectionSettings] = useState({
    heartbeat_interval: '5m',
    performance_interval: '24h',
    hardware_interval: '72h',
    software_interval: '48h',
    user_interval: '6h',
    network_interval: '24h',
    security_interval: '12h'
  });
  
  const [originalSettings, setOriginalSettings] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    // Check if settings have changed
    if (originalSettings) {
      const changed = JSON.stringify(collectionSettings) !== JSON.stringify(originalSettings);
      setHasChanges(changed);
    }
  }, [collectionSettings, originalSettings]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await apiService.getCollectionSettings();
      if (response && response.success && response.data) {
        const settings = response.data.settings || response.data;
        setCollectionSettings(settings);
        setOriginalSettings(JSON.parse(JSON.stringify(settings)));
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
      setNotification({
        type: 'error',
        message: 'Failed to load settings. Using defaults.'
      });
      setTimeout(() => setNotification(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setCollectionSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const validateInterval = (value) => {
    // Format: number followed by s, m, h, or d (e.g., "5m", "24h", "72h")
    const pattern = /^\d+[smhd]$/i;
    return pattern.test(value);
  };

  const formatIntervalLabel = (value) => {
    if (!value) return '';
    const match = value.match(/^(\d+)([smhd])$/i);
    if (!match) return value;
    
    const num = parseInt(match[1]);
    const unit = match[2].toLowerCase();
    
    const unitLabels = {
      's': 'second(s)',
      'm': 'minute(s)',
      'h': 'hour(s)',
      'd': 'day(s)'
    };
    
    return `${num} ${unitLabels[unit] || unit}`;
  };

  const handleSave = async () => {
    // Validate all intervals
    const invalidFields = [];
    Object.entries(collectionSettings).forEach(([key, value]) => {
      if (key.endsWith('_interval') && !validateInterval(value)) {
        invalidFields.push(key);
      }
    });

    if (invalidFields.length > 0) {
      setNotification({
        type: 'error',
        message: `Invalid interval format for: ${invalidFields.join(', ')}. Use format like "5m", "24h", "72h"`
      });
      setTimeout(() => setNotification(null), 5000);
      return;
    }

    setSaving(true);
    try {
      const response = await apiService.updateCollectionSettings(collectionSettings);
      if (response && response.success) {
        setOriginalSettings(JSON.parse(JSON.stringify(collectionSettings)));
        setNotification({
          type: 'success',
          message: 'Settings saved successfully! Agents will use these settings on their next check-in.'
        });
        setTimeout(() => setNotification(null), 5000);
      } else {
        throw new Error(response?.error || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      setNotification({
        type: 'error',
        message: error.message || 'Failed to save settings. Please try again.'
      });
      setTimeout(() => setNotification(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (originalSettings) {
      setCollectionSettings(JSON.parse(JSON.stringify(originalSettings)));
      setNotification({
        type: 'info',
        message: 'Settings reset to last saved values.'
      });
      setTimeout(() => setNotification(null), 3000);
    }
  };

  const intervalFields = [
    {
      key: 'heartbeat_interval',
      label: 'Heartbeat Interval',
      description: 'How often agents send heartbeat signals to the server',
      icon: Heart,
      color: 'text-red-500',
      default: '5m',
      help: 'Recommended: 5m (5 minutes)'
    },
    {
      key: 'performance_interval',
      label: 'Performance Metrics',
      description: 'How often to collect CPU, memory, disk usage data',
      icon: Activity,
      color: 'text-blue-500',
      default: '24h',
      help: 'Recommended: 24h (1 day)'
    },
    {
      key: 'hardware_interval',
      label: 'Hardware Inventory',
      description: 'How often to collect hardware information (CPU, RAM, storage)',
      icon: HardDrive,
      color: 'text-purple-500',
      default: '72h',
      help: 'Recommended: 72h (3 days)'
    },
    {
      key: 'software_interval',
      label: 'Software Inventory',
      description: 'How often to collect installed software and applications',
      icon: Package,
      color: 'text-green-500',
      default: '48h',
      help: 'Recommended: 48h (2 days)'
    },
    {
      key: 'user_interval',
      label: 'User Inventory',
      description: 'How often to collect user account and login information',
      icon: Users,
      color: 'text-orange-500',
      default: '6h',
      help: 'Recommended: 6h (6 hours)'
    },
    {
      key: 'network_interval',
      label: 'Network Inventory',
      description: 'How often to collect network configuration and connectivity data',
      icon: Network,
      color: 'text-indigo-500',
      default: '24h',
      help: 'Recommended: 24h (1 day)'
    },
    {
      key: 'security_interval',
      label: 'Security Inventory',
      description: 'How often to collect security-related information',
      icon: Shield,
      color: 'text-yellow-500',
      default: '12h',
      help: 'Recommended: 12h (12 hours)'
    }
  ];

  const content = (
    <>
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {notification && (
            <div
              className={`mb-4 p-4 rounded-lg border flex items-center gap-3 ${
                notification.type === 'success'
                  ? 'bg-green-50 border-green-200 text-green-800'
                  : notification.type === 'error'
                  ? 'bg-red-50 border-red-200 text-red-800'
                  : 'bg-blue-50 border-blue-200 text-blue-800'
              }`}
            >
              {notification.type === 'success' ? (
                <CheckCircle size={20} />
              ) : notification.type === 'error' ? (
                <AlertCircle size={20} />
              ) : (
                <AlertCircle size={20} />
              )}
              <span className="text-sm font-medium">{notification.message}</span>
            </div>
          )}

          {activeTab === 'client-settings' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Data Collection Frequencies</h3>
                <p className="text-sm text-slate-600 mb-6">
                  Configure how often agents collect different types of data from managed devices.
                  Use interval format: <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">5m</code> (5 minutes),{' '}
                  <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">24h</code> (24 hours),{' '}
                  <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">72h</code> (3 days)
                </p>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw size={24} className="animate-spin text-blue-600" />
                  <span className="ml-3 text-slate-600">Loading settings...</span>
                </div>
              ) : (
                <div className="space-y-4">
                  {intervalFields.map((field) => {
                    const Icon = field.icon;
                    const value = collectionSettings[field.key] || field.default;
                    const isValid = validateInterval(value);
                    
                    return (
                      <div
                        key={field.key}
                        className="bg-white border border-slate-200 rounded-lg p-5 hover:border-blue-300 transition-colors"
                      >
                        <div className="flex items-start gap-4">
                          <div className={`p-3 rounded-lg bg-slate-50 ${field.color}`}>
                            <Icon size={20} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <label className="text-sm font-semibold text-slate-900">
                                {field.label}
                              </label>
                              {!isValid && (
                                <span className="text-xs text-red-600 font-medium">Invalid format</span>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 mb-3">{field.description}</p>
                            
                            <div className="flex items-center gap-3">
                              <div className="flex-1">
                                <input
                                  type="text"
                                  value={value}
                                  onChange={(e) => handleSettingChange(field.key, e.target.value)}
                                  placeholder={field.default}
                                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 ${
                                    isValid
                                      ? 'border-slate-300 focus:ring-blue-500 focus:border-blue-500'
                                      : 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                  }`}
                                />
                                {value && isValid && (
                                  <p className="text-xs text-slate-500 mt-1">
                                    {formatIntervalLabel(value)}
                                  </p>
                                )}
                                {value && !isValid && (
                                  <p className="text-xs text-red-600 mt-1">
                                    Format: number + unit (s/m/h/d). Example: 5m, 24h, 72h
                                  </p>
                                )}
                              </div>
                              <div className="text-xs text-slate-400">
                                <Clock size={16} className="inline mr-1" />
                                {field.help}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50">
          <div className="text-xs text-slate-500">
            {hasChanges && <span className="text-amber-600 font-medium">You have unsaved changes</span>}
            {!hasChanges && <span>All changes saved</span>}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleReset}
              disabled={!hasChanges || loading}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Reset
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving || loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {saving ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Save Settings
                </>
              )}
            </button>
          </div>
        </div>
    </>
  );

  if (embedded) {
    return <div className="flex flex-col min-h-0">{content}</div>;
  }

  return (
    <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <SettingsIcon size={24} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Settings</h2>
              <p className="text-sm text-slate-500">Configure system and agent settings</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 bg-white">
          <button
            onClick={() => setActiveTab('client-settings')}
            className={`px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'client-settings'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            Client/Agent Settings
          </button>
        </div>

        {content}
      </div>
    </div>
  );
};

export default Settings;

